import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, RotateCcw, Cpu, Activity } from 'lucide-react';

const FEATURE_LABELS = {
  jha_full_generation: 'Full / complex JHA generation',
  audit_photo_analysis: 'Audit photo analysis',
  daily_jsa_mitigation: 'Daily JSA single-hazard mitigation',
  daily_jsa_organize: 'Daily JSA — organize sourced content (Luna)',
  daily_jsa_gap_fill: 'Daily JSA — develop missing content (Sol)',
  manual_sop_qa: 'Safety manual / SOP Q&A',
  incident_narrative: 'Incident narrative',
  sds_extraction: 'SDS extraction',
  sds_qa: 'SDS Q&A',
  translation: 'Translation (EN/ES)',
  classification_tagging: 'Classification / tagging',
  simple_rewrite: 'Simple rewrites',
  safety_assistant_chat: 'Safety assistant chat',
};

export default function AiModelControls() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const [{ data }, { data: tel }] = await Promise.all([
        axiosInstance.get('/ai-config'),
        axiosInstance.get('/ai-config/telemetry', { params: { limit: 25 } }),
      ]);
      setConfig(data);
      setTelemetry(tel.calls || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load AI controls');
    }
  };

  useEffect(() => { load(); }, []);

  const setModel = async (feature, model) => {
    setBusy(feature);
    try {
      const { data } = await axiosInstance.put(`/ai-config/feature/${feature}`, { model });
      toast.success(`${FEATURE_LABELS[feature] || feature} → ${data.effectiveModel}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setBusy('');
    }
  };

  const resetModel = async (feature) => {
    setBusy(feature);
    try {
      const { data } = await axiosInstance.put(`/ai-config/feature/${feature}`, { reset: true });
      toast.success(`Reverted to profile default → ${data.effectiveModel}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setBusy('');
    }
  };

  const toggleFlag = async (flag, enabled) => {
    setBusy(flag);
    try {
      await axiosInstance.put(`/ai-config/flag/${flag}`, { enabled });
      toast.success(`${flag} ${enabled ? 'enabled' : 'disabled'}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toggle failed');
    } finally {
      setBusy('');
    }
  };

  if (!config) {
    return <div className="p-8 text-slate-500" data-testid="ai-controls-loading">Loading AI controls…</div>;
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6" data-testid="ai-model-controls-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/superadmin')} data-testid="ai-controls-back">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AI Model Controls</h1>
          <p className="text-sm text-slate-500">
            Active profile <span className="font-medium">{config.profile}</span> · each feature
            reverts independently without touching the others
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4" /> Feature switches
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {config.flags.map(f => (
            <div key={f.flag} className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">{f.flag}</p>
                <p className="text-xs text-slate-500">{f.description}</p>
              </div>
              <Switch
                checked={f.enabled}
                disabled={busy === f.flag}
                onCheckedChange={(v) => toggleFlag(f.flag, v)}
                data-testid={`ai-flag-${f.flag}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Cpu className="h-4 w-4" /> Model per feature
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {config.features.map(f => (
            <div key={f.feature}
                 className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                 data-testid={`ai-feature-row-${f.feature}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium">{FEATURE_LABELS[f.feature] || f.feature}</p>
                <p className="text-xs text-slate-500">
                  profile default: {f.profileModel}
                  {f.provider === 'byo' ? ' · BYO key' : ''}
                  {f.fallbackChain.length ? ` · falls back to ${f.fallbackChain.join(' → ')}` : ''}
                  {f.overridden ? ' · OVERRIDDEN' : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={f.effectiveModel} onValueChange={(v) => setModel(f.feature, v)}>
                  <SelectTrigger className="w-[190px]" data-testid={`ai-model-select-${f.feature}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config.availableModels.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" disabled={!f.overridden || busy === f.feature}
                        onClick={() => resetModel(f.feature)}
                        data-testid={`ai-model-reset-${f.feature}`}>
                  <RotateCcw className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent routed calls</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="ai-telemetry-table">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-1 pr-3">When</th>
                  <th className="py-1 pr-3">Feature</th>
                  <th className="py-1 pr-3">Asked for</th>
                  <th className="py-1 pr-3">Actually used</th>
                  <th className="py-1 pr-3">Fallback</th>
                  <th className="py-1 pr-3">Latency</th>
                </tr>
              </thead>
              <tbody>
                {telemetry.map((c, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-1 pr-3 whitespace-nowrap">{String(c.timestamp || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="py-1 pr-3">{c.feature}</td>
                    <td className="py-1 pr-3">{c.primary_model}</td>
                    <td className="py-1 pr-3 font-medium">{c.actual_model || '—'}</td>
                    <td className="py-1 pr-3">{c.fallback_used ? (c.fallback_reason || 'yes') : 'no'}</td>
                    <td className="py-1 pr-3">{c.latency_s ? `${c.latency_s}s` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
